import dayjs, { type Dayjs } from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import utc from "dayjs/plugin/utc"

import { DATE_FORMAT } from "../../../../utils/date/date.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"

dayjs.extend(utc)
dayjs.extend(customParseFormat)

/**
 * A row the API has never seen carries this prefix instead of a UUID; the PATCH
 * strips the whole `id`, which is what tells the serializer to create it.
 */
const DRAFT_MILESTONE_PREFIX = "_"

const isDraftMilestone = (phase?: LeadFormDataPhase | null) =>
  !!phase?.id?.startsWith(DRAFT_MILESTONE_PREFIX)

const sortMilestones = (phases?: LeadFormDataPhase[] | null) =>
  [...(phases ?? [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))

const createMilestone = (
  order: number,
  projectTypeId?: string | null
): LeadFormDataPhase => ({
  id: `${DRAFT_MILESTONE_PREFIX}${crypto.randomUUID()}`,
  name: "",
  order,
  budget: { budget: 0 },
  project_type: projectTypeId ?? "",
  checklist_data: []
})

const milestonePrice = (phase?: LeadFormDataPhase | null) => {
  const price = Number(phase?.budget?.budget ?? 0)
  return Number.isFinite(price) ? price : 0
}

const withMilestonePrice = (
  phase: LeadFormDataPhase,
  price?: number | null
): LeadFormDataPhase => ({
  ...phase,
  budget: { ...phase?.budget, budget: price ?? 0 }
})

const milestoneDuration = (phase?: LeadFormDataPhase | null) => {
  const duration = Number(phase?.duration_days ?? 0)
  return Number.isFinite(duration) ? duration : 0
}

/**
 * The wire format is a bare calendar day, so the maths runs in UTC where a
 * day is always 24 hours — a local-time diff across a DST switch is off by one.
 */
const parseMilestoneDay = (value?: string | null): Dayjs | null => {
  if (!value) return null
  const day = dayjs.utc(value, DATE_FORMAT, true)
  return day.isValid() ? day : null
}

const formatMilestoneDay = (day: Dayjs) => day.format(DATE_FORMAT)

/** Inclusive: a milestone that starts and ends on the same day lasts one day. */
const milestoneDaysBetween = (start: Dayjs, end: Dayjs) =>
  end.diff(start, "day") + 1

/**
 * Last edit wins, and one edit derives at most one other field: a date fills
 * the duration when the opposite date is there, and a duration fills whichever
 * date is missing — the end when a start exists, else the start from the end.
 * An end before its start yields no duration rather than a negative one, and
 * clearing any field touches nothing else.
 */
const withMilestoneStartDate = (
  phase: LeadFormDataPhase,
  startDate?: string | null
): LeadFormDataPhase => {
  const next = { ...phase, start_date: startDate ?? null }
  const start = parseMilestoneDay(startDate)
  const end = parseMilestoneDay(phase.end_date)
  if (!start || !end) return next

  const days = milestoneDaysBetween(start, end)
  return days > 0 ? { ...next, duration_days: days } : next
}

const withMilestoneEndDate = (
  phase: LeadFormDataPhase,
  endDate?: string | null
): LeadFormDataPhase => {
  const next = { ...phase, end_date: endDate ?? null }
  const start = parseMilestoneDay(phase.start_date)
  const end = parseMilestoneDay(endDate)
  if (!start || !end) return next

  const days = milestoneDaysBetween(start, end)
  return days > 0 ? { ...next, duration_days: days } : next
}

const withMilestoneDuration = (
  phase: LeadFormDataPhase,
  duration?: number | null
): LeadFormDataPhase => {
  const next = { ...phase, duration_days: duration ?? null }
  if (duration == null || !Number.isFinite(duration)) return next

  const days = Math.floor(duration)
  if (days < 1) return next

  const start = parseMilestoneDay(phase.start_date)
  if (start) {
    return { ...next, end_date: formatMilestoneDay(start.add(days - 1, "day")) }
  }

  const end = parseMilestoneDay(phase.end_date)
  if (end) {
    return {
      ...next,
      start_date: formatMilestoneDay(end.subtract(days - 1, "day"))
    }
  }

  return next
}

const milestoneReceivable = (phase?: LeadFormDataPhase | null) => {
  const value = Number(phase?.planned_receivable ?? 0)
  return Number.isFinite(value) ? value : 0
}

const milestonePayable = (phase?: LeadFormDataPhase | null) => {
  const value = Number(phase?.planned_payable ?? 0)
  return Number.isFinite(value) ? value : 0
}

const withMilestoneReceivable = (
  phase: LeadFormDataPhase,
  value?: number | null
): LeadFormDataPhase => ({
  ...phase,
  planned_receivable: value ?? 0
})

const withMilestonePayable = (
  phase: LeadFormDataPhase,
  value?: number | null
): LeadFormDataPhase => ({
  ...phase,
  planned_payable: value ?? 0
})

const milestonesReceivableTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestoneReceivable(phase), 0)

const milestonesPayableTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestonePayable(phase), 0)

const milestonePayableExceedsReceivable = (phase?: LeadFormDataPhase | null) =>
  milestonePayable(phase) > milestoneReceivable(phase)

const isMilestoneNamed = (phase?: LeadFormDataPhase | null) =>
  !!phase?.name?.trim()

const milestonesAreNamed = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).every(isMilestoneNamed)

const milestonesTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestonePrice(phase), 0)

const milestonesDurationTotal = (phases?: LeadFormDataPhase[] | null) =>
  (phases ?? []).reduce((total, phase) => total + milestoneDuration(phase), 0)

const moveMilestone = (
  phases: LeadFormDataPhase[],
  from: number,
  to: number
): LeadFormDataPhase[] => {
  if (from === to || from < 0 || to < 0 || from >= phases.length) return phases
  if (to >= phases.length) return phases

  const moved = [...phases]
  const [row] = moved.splice(from, 1)
  moved.splice(to, 0, row)

  return moved.map((phase, index) => ({ ...phase, order: index }))
}

/**
 * `order` is re-derived from the row's place in the table, so a reorder that
 * never touched a row still persists its new position.
 */
const serializeMilestonesForPatch = (
  phases?: LeadFormDataPhase[] | null
): LeadFormDataPhase[] =>
  (phases ?? []).map(({ id, project_type, ...phase }, index) => ({
    ...phase,
    order: index,
    ...(project_type ? { project_type } : {}),
    ...(id?.startsWith(DRAFT_MILESTONE_PREFIX) ? {} : { id })
  })) as LeadFormDataPhase[]

export {
  DRAFT_MILESTONE_PREFIX,
  isDraftMilestone,
  sortMilestones,
  createMilestone,
  milestonePrice,
  withMilestonePrice,
  milestoneDuration,
  withMilestoneStartDate,
  withMilestoneEndDate,
  withMilestoneDuration,
  milestoneReceivable,
  milestonePayable,
  withMilestoneReceivable,
  withMilestonePayable,
  milestonesReceivableTotal,
  milestonesPayableTotal,
  milestonePayableExceedsReceivable,
  isMilestoneNamed,
  milestonesAreNamed,
  milestonesTotal,
  milestonesDurationTotal,
  moveMilestone,
  serializeMilestonesForPatch
}
